import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import type { Challenge, ChallengeResult, DisciplineSlug } from "@jumpbean/shared";
import { ChallengeEngine } from "../components/challenges/ChallengeEngine";
import { ChallengeLoadingState } from "../components/challenges/ChallengeLoadingState";
import { apiGet, apiPost } from "../lib/api";
import { useGameStore } from "../stores/gameStore";
import { useAppStore } from "../stores/appStore";
import { trackEvent } from "@jumpbean/analytics";

interface ChallengeApiResponse {
  id: string;
  challengeNumber: number;
  type: string;
  content: Record<string, unknown>;
  difficulty: number;
  isActive: boolean;
  level: {
    levelNumber: number;
    name: string;
  };
  discipline: {
    slug: string;
    name: string;
    characterName: string;
    characterImageUrl: string;
  } | null;
}

/**
 * ChallengePage - Active challenge gameplay.
 *
 * Features:
 * - Full-screen challenge interface
 * - Loads challenge data from API
 * - Uses ChallengeEngine for gameplay
 * - Tracks progress and stars earned
 */
interface LocationState {
  discipline?: DisciplineSlug;
  characterName?: string;
}

export function ChallengePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // Get initial values from navigation state (if available) to avoid flash
  const locationState = location.state as LocationState | null;

  const { currentChild, loadProgress } = useGameStore();
  const selectedChildId = useAppStore((state) => state.selectedChildId);
  const childId = currentChild?.id || selectedChildId;

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [discipline, setDiscipline] = useState<DisciplineSlug>(locationState?.discipline ?? "math");
  const [characterName, setCharacterName] = useState<string>(locationState?.characterName ?? "");
  const [characterImageUrl, setCharacterImageUrl] = useState<string>("");
  const [levelNumber, setLevelNumber] = useState<number>(1);
  const [challengeNumber, setChallengeNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasTrackedStart = useRef(false);

  // Redirect if no child selected
  useEffect(() => {
    if (!childId) {
      navigate("/profiles");
    }
  }, [childId, navigate]);

  // Load challenge data
  useEffect(() => {
    if (!id) return;

    const loadChallenge = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiGet<ChallengeApiResponse>(
          `/api/content/challenges/${id}`
        );

        if (response.error) {
          setError(response.error.message);
          return;
        }

        if (response.data) {
          const data = response.data;

          // Transform API response to Challenge type
          const challengeData: Challenge = {
            id: data.id,
            levelId: "", // Not needed for rendering
            challengeNumber: data.challengeNumber,
            type: data.type as Challenge["type"],
            content: data.content,
            difficulty: data.difficulty,
            isActive: data.isActive,
          };

          setChallenge(challengeData);
          setLevelNumber(data.level.levelNumber);
          setChallengeNumber(data.challengeNumber);

          if (data.discipline) {
            setDiscipline(data.discipline.slug as DisciplineSlug);
            setCharacterName(data.discipline.characterName);
            setCharacterImageUrl(data.discipline.characterImageUrl);
          }

          // Track challenge start (only once per challenge load)
          if (!hasTrackedStart.current && childId) {
            hasTrackedStart.current = true;
            trackEvent("challenge_started", {
              challenge_id: data.id,
              discipline: data.discipline?.slug as DisciplineSlug ?? "math",
              level_number: data.level.levelNumber,
              challenge_number: data.challengeNumber,
              challenge_type: data.type,
              child_id: childId,
            });
          }
        }
      } catch (err) {
        console.error("Failed to load challenge:", err);
        setError("Failed to load challenge");
      } finally {
        setIsLoading(false);
      }
    };

    loadChallenge();
  }, [id]);

  // Handle challenge completion
  const handleComplete = useCallback(
    async (result: ChallengeResult) => {
      if (!childId || !challenge) return;

      // Track challenge completion
      trackEvent("challenge_completed", {
        challenge_id: result.challengeId,
        discipline,
        level_number: levelNumber,
        challenge_number: challengeNumber,
        challenge_type: challenge.type,
        stars_earned: result.starsEarned,
        attempts: result.attempts,
        time_seconds: result.timeSpentSeconds,
        correct: result.correct,
        child_id: childId,
      });

      try {
        // Record progress to API
        await apiPost(`/api/progress/${childId}/progress`, {
          challengeId: result.challengeId,
          starsEarned: result.starsEarned,
          attempts: result.attempts,
          timeSpentSeconds: result.timeSpentSeconds,
        });

        // Reload progress from API to update cachedProgress in the store
        await loadProgress(childId);
      } catch (err) {
        console.error("Failed to record progress:", err);
      }

      // Navigate back to level page after a short delay
      setTimeout(() => {
        navigate(`/play/${discipline}/${levelNumber}`);
      }, 1500);
    },
    [childId, challenge, discipline, levelNumber, challengeNumber, navigate, loadProgress]
  );

  // Handle skip
  const handleSkip = useCallback(() => {
    if (childId && challenge) {
      trackEvent("challenge_skipped", {
        challenge_id: challenge.id,
        discipline,
        level_number: levelNumber,
        child_id: childId,
      });
    }
    navigate(`/play/${discipline}/${levelNumber}`);
  }, [childId, challenge, discipline, levelNumber, navigate]);

  // Handle back - navigate to previous page
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Show loading state
  if (isLoading) {
    return (
      <ChallengeLoadingState
        discipline={discipline}
        characterName={characterName}
        message="Loading challenge..."
      />
    );
  }

  // Show error state
  if (error || !challenge) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <p className="font-display text-display-sm text-coral-600 mb-4">
            Oops!
          </p>
          <p className="font-body text-body-lg text-neutral-600 mb-6">
            {error || "Challenge not found"}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-magic-500 text-white font-display font-bold rounded-full hover:bg-magic-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <ChallengeEngine
      challenge={challenge}
      onComplete={handleComplete}
      onSkip={handleSkip}
      onBack={handleBack}
      characterName={characterName}
      characterImageUrl={characterImageUrl}
      discipline={discipline}
      showSkip={false}
    />
  );
}

export default ChallengePage;
